import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useTheme } from "../../contexts/ThemeContext";

interface AllowedIP {
  id: number;
  ip_address: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

const IPManagement: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [ips, setIps] = useState<AllowedIP[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIP, setCurrentIP] = useState<string>("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    ip_address: "",
    description: "",
    is_active: true,
  });

  const API_URL =
    process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

  useEffect(() => {
    loadIPs();
    checkCurrentIP();
  }, []);

  const checkCurrentIP = async () => {
    try {
      const response = await axios.get(`${API_URL}/ip-access/check`);
      setCurrentIP(response.data.client_ip);
    } catch (error) {
      console.error("Error checking current IP:", error);
    }
  };

  const loadIPs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/ip-access?include_inactive=true`
      );
      setIps(response.data);
    } catch (error) {
      console.error("Error loading IPs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddIP = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/ip-access`, {
        ...formData,
        created_by: "admin",
      });
      setShowAddForm(false);
      setFormData({ ip_address: "", description: "", is_active: true });
      loadIPs();
      enqueueSnackbar("IP added successfully", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || "Error adding IP", {
        variant: "error",
      });
    }
  };

  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      await axios.put(`${API_URL}/ip-access/${id}`, {
        is_active: !isActive,
      });
      loadIPs();
    } catch (error) {
      console.error("Error toggling IP:", error);
    }
  };

  const handleDeleteIP = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this IP?")) return;

    try {
      await axios.delete(`${API_URL}/ip-access/${id}`);
      loadIPs();
    } catch (error) {
      console.error("Error deleting IP:", error);
    }
  };

  const handleUseCurrentIP = () => {
    setFormData({ ...formData, ip_address: currentIP });
    setShowAddForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className={`p-4 ${isDarkMode ? "bg-gray-900" : "bg-transparent"}`}>
      <div className="max-w-6xl mx-auto">
        {/* Current IP Info */}
        {currentIP && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              isDarkMode
                ? "bg-blue-900/30 border border-blue-700"
                : "bg-blue-50 border border-blue-200"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-blue-400" : "text-blue-600"
                  }`}
                >
                  Your Current IP
                </p>
                <p
                  className={`text-lg font-mono font-bold ${
                    isDarkMode ? "text-blue-300" : "text-blue-900"
                  }`}
                >
                  {currentIP}
                </p>
              </div>
              {!showAddForm && (
                <button
                  onClick={handleUseCurrentIP}
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${
                    isDarkMode
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  Add this IP
                </button>
              )}
            </div>
          </div>
        )}

        {/* Add IP Button */}
        {!showAddForm && (
          <div className="mb-4">
            <button
              onClick={() => setShowAddForm(true)}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                isDarkMode
                  ? "bg-phoenix-600 text-white hover:bg-phoenix-700"
                  : "bg-phoenix-600 text-white hover:bg-phoenix-700"
              }`}
            >
              + Add New IP
            </button>
          </div>
        )}

        {/* Add IP Form */}
        {showAddForm && (
          <div
            className={`mb-4 p-4 rounded-lg shadow-sm ${
              isDarkMode
                ? "bg-gray-800 border border-gray-700"
                : "bg-white border border-gray-200"
            }`}
          >
            <h2
              className={`text-base font-semibold mb-3 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Add New IP
            </h2>
            <form onSubmit={handleAddIP}>
              <div className="space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    IP Address *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.ip_address}
                      onChange={(e) =>
                        setFormData({ ...formData, ip_address: e.target.value })
                      }
                      placeholder="Ex: 192.168.1.100 or 0.0.0.0 for all"
                      className={`flex-1 px-3 py-2 rounded-md focus:outline-none focus:ring-2 transition-colors ${
                        isDarkMode
                          ? "bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:ring-phoenix-500 focus:border-phoenix-500"
                          : "bg-white border border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-phoenix-500 focus:border-phoenix-500"
                      }`}
                      required
                    />
                    {currentIP && (
                      <button
                        type="button"
                        onClick={handleUseCurrentIP}
                        className={`px-3 py-2 rounded-md text-sm ${
                          isDarkMode
                            ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Use current
                      </button>
                    )}
                  </div>
                  <p
                    className={`text-xs mt-1 ${
                      isDarkMode ? "text-gray-500" : "text-gray-500"
                    }`}
                  >
                    Use 0.0.0.0 to allow all IPs (wildcard)
                  </p>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Ex: Main office, Home, VPN, etc."
                    className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 transition-colors ${
                      isDarkMode
                        ? "bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:ring-phoenix-500 focus:border-phoenix-500"
                        : "bg-white border border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-phoenix-500 focus:border-phoenix-500"
                    }`}
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="is_active"
                    className={`ml-2 text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Active (allow access immediately)
                  </label>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  type="submit"
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${
                    isDarkMode
                      ? "bg-phoenix-600 text-white hover:bg-phoenix-700"
                      : "bg-phoenix-600 text-white hover:bg-phoenix-700"
                  }`}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setFormData({
                      ip_address: "",
                      description: "",
                      is_active: true,
                    });
                  }}
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${
                    isDarkMode
                      ? "bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600"
                      : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                  }`}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* IPs List */}
        <div
          className={`rounded-lg shadow-sm overflow-hidden ${
            isDarkMode
              ? "bg-gray-800 border border-gray-700"
              : "bg-white border border-gray-200"
          }`}
        >
          <table
            className={`min-w-full divide-y ${
              isDarkMode ? "divide-gray-700" : "divide-gray-200"
            }`}
          >
            <thead className={isDarkMode ? "bg-gray-700" : "bg-gray-50"}>
              <tr>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  IP Address
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Description
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Status
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Created
                </th>
                <th
                  className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody
              className={`divide-y ${
                isDarkMode
                  ? "bg-gray-800 divide-gray-700"
                  : "bg-white divide-gray-200"
              }`}
            >
              {ips.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className={`px-6 py-8 text-center ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No configured IPs
                  </td>
                </tr>
              ) : (
                ips.map((ip) => (
                  <tr
                    key={ip.id}
                    className={`${
                      isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span
                          className={`text-sm font-mono font-semibold ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {ip.ip_address}
                        </span>
                        {ip.ip_address === currentIP && (
                          <span
                            className={`ml-2 px-2 py-1 text-xs rounded-full ${
                              isDarkMode
                                ? "bg-blue-900/50 text-blue-300"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            Your IP
                          </span>
                        )}
                        {ip.ip_address === "0.0.0.0" && (
                          <span
                            className={`ml-2 px-2 py-1 text-xs rounded-full ${
                              isDarkMode
                                ? "bg-yellow-900/50 text-yellow-300"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            Wildcard
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`text-sm ${
                          isDarkMode ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {ip.description || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(ip.id, ip.is_active)}
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          ip.is_active
                            ? isDarkMode
                              ? "bg-green-900/50 text-green-300"
                              : "bg-green-100 text-green-800"
                            : isDarkMode
                            ? "bg-red-900/50 text-red-300"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {ip.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {new Date(ip.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDeleteIP(ip.id)}
                        className={`${
                          isDarkMode
                            ? "text-red-400 hover:text-red-300"
                            : "text-red-600 hover:text-red-900"
                        }`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Help Section */}
        <div
          className={`mt-6 p-4 rounded-lg border ${
            isDarkMode
              ? "bg-gray-800/50 border-gray-700"
              : "bg-blue-50 border-blue-200"
          }`}
        >
          <h3
            className={`text-sm font-semibold mb-2 flex items-center ${
              isDarkMode ? "text-gray-200" : "text-gray-900"
            }`}
          >
            <InformationCircleIcon
              className={`h-5 w-5 mr-1 ${
                isDarkMode ? "text-blue-400" : "text-blue-600"
              }`}
            />
            Important information
          </h3>
          <ul
            className={`text-sm space-y-1 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            <li>• Only active IPs will have access to the Inspection Board</li>
            <li>
              • Use{" "}
              <code
                className={`px-1 rounded font-mono ${
                  isDarkMode
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                0.0.0.0
              </code>{" "}
              to temporarily allow all IPs
            </li>
            <li>• If using VPN, add your VPN's outgoing IP</li>
            <li>• IPs are verified on each Inspection Board load</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default IPManagement;
